import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import {
  Calendar,
  Clock,
  Plus,
  Search,
  Filter,
  CheckCircle,
  AlertCircle,
  Users,
  FileText,
  Edit,
  Trash2,
  Eye,
  ArrowRight,
  Flag,
  Building,
  User,
  ChevronDown,
  ChevronRight,
  MoreHorizontal,
  MoreVertical,
  UserCheck,
  MapPin,
  Target,
  Activity,
  RefreshCw,
  Download,
  Upload,
  Grid,
  List,
  ChevronLeft,
  ChevronUp,
  ChevronDown as ChevronDownIcon,
  X
} from 'lucide-react';
import ActivityForm from './ActivityForm';
import ActivityDetail from './ActivityDetail';
import EntityFilter from '../../components/EntityManagement/EntityFilter';
import { useTenantProtection } from '../../hooks/useTenantProtection';
import { useTranslationReady } from '../../hooks/useTranslationReady';
import { api } from '../../utils/api';

const Activities = () => {
  const { t, i18n, ready, translationsReady, safeT } = useTranslationReady([
    'activities.title',
    'activities.addActivity',
    'activities.searchPlaceholder',
    'activities.status',
    'activities.priority',
    'activities.entityTypes.customer',
    'activities.entityTypes.lead',
    'activities.entityTypes.opportunity',
    'activities.entityTypes.activity'
  ]);
  const location = useLocation();
  const params = useParams();
  const { getProtectedTenantCode } = useTenantProtection();
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [editingActivity, setEditingActivity] = useState(null);
  const [stats, setStats] = useState({});
  const [focusedActivityId, setFocusedActivityId] = useState(null);
  const [hasScrolledToActivity, setHasScrolledToActivity] = useState(false);
  const activityRefs = useRef({});
  const [relatedEntitiesMap, setRelatedEntitiesMap] = useState({});

     // Filters and pagination
   const [filters, setFilters] = useState({
     search: '',
     status: '',
     priority: '',
     entityType: '',
     assignedTo: '',
     overdue: false,
     upcoming: false,
     completed: false,
     inProgress: false
   });
   
   // Advanced filters for EntityFilter component
   const [advancedFilters, setAdvancedFilters] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [menuExpanded, setMenuExpanded] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  // Calendar view state
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'calendar'
  const [calendarView, setCalendarView] = useState('month'); // 'year', 'month', 'week'
  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarEvents, setCalendarEvents] = useState([]);

  // Export modal state
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportActivities, setExportActivities] = useState([]);
  const [selectedExportIds, setSelectedExportIds] = useState(new Set());

  // Load activities
  const loadActivities = async () => {
    setLoading(true);
    setError(null);

    try {
      const queryParams = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, value]) => value !== '' && value !== false)
        )
      });
      
      // Add advanced filters if any
      const validFilters = advancedFilters.filter(f => 
        f.field && f.operator && (f.operator === 'isNull' || f.operator === 'isNotNull' || (f.value !== undefined && f.value !== ''))
      );
      if (validFilters.length > 0) {
        queryParams.set('filters', JSON.stringify(validFilters));
      }

      const data = await api.get(`/activities?${queryParams}`, {
        cacheTtlMs: 3000
      });
      const list = data.activities || [];
      setActivities(list);
      setPagination(prev => ({
        ...prev,
        total: data.pagination?.total || 0,
        pages: data.pagination?.pages || 0
      }));

      // Calculate stats from loaded activities to ensure UI reflects actual data shown
      if (Array.isArray(list)) {
        const now = new Date();
        const nextStats = {
          total: list.length,
          completed: 0,
          inProgress: 0,
          overdue: 0,
          upcoming: 0,
          critical: 0
        };

        list.forEach((activity) => {
          if (activity.Status === 'Done') {
            nextStats.completed += 1;
          }
          if (activity.Status === 'In Progress') {
            nextStats.inProgress += 1;
          }
          if (activity.Priority === 'Critical') {
            nextStats.critical += 1;
          }
          if (!activity.DueDate || activity.Status === 'Done' || activity.Status === 'Cancelled') {
            return;
          }
          const dueDate = new Date(activity.DueDate);
          if (dueDate < now) {
            nextStats.overdue += 1;
            return;
          }
          const daysUntilDue = Math.ceil((dueDate - now) / (1000 * 60 * 60 * 24));
          if (daysUntilDue > 0 && daysUntilDue <= 7) {
            nextStats.upcoming += 1;
          }
        });

        setStats(nextStats);
      }

    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Load statistics
  const loadStats = async () => {
    try {
      const data = await api.get('/activities/stats/summary', {
        cacheTtlMs: 5000
      });
      setStats(data.stats || {});

    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  };

  const filtersKey = useMemo(() => JSON.stringify(filters), [filters]);
  const listDepsRef = useRef({ filtersKey });

  // Load data on component mount and when filters/page change
  useEffect(() => {
    const filtersChanged = listDepsRef.current.filtersKey !== filtersKey;
    listDepsRef.current = { filtersKey };
    if (filtersChanged && pagination.page !== 1) {
      setPagination(prev => ({ ...prev, page: 1 }));
      return;
    }
    loadActivities();
  }, [filtersKey, pagination.page]);

  // Auto-show filter bar when filters are active
  useEffect(() => {
    const hasActiveFilters = Object.values(filters).some(value => value !== '' && value !== false);
    if (hasActiveFilters) {
      setShowFilters(true);
    }
  }, [filters]);

  // Fetch and cache related entities for listed activities
  useEffect(() => {
    const missingIds = activities
      .filter(a => relatedEntitiesMap[a.ActivityID] === undefined)
      .map(a => a.ActivityID);
    if (missingIds.length === 0) return;

    const tenantCode = getProtectedTenantCode();
    const selectedRole = localStorage.getItem('selectedRole');
    (async () => {
      for (const id of missingIds) {
        try {
          const resp = await fetch(`/api/activities/${id}`, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {}),
              ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {})
            }
          });
          if (resp.ok) {
            const data = await resp.json();
            const rels = data.activity?.relatedEntities || [];
            setRelatedEntitiesMap(prev => ({ ...prev, [id]: rels }));
          } else {
            setRelatedEntitiesMap(prev => ({ ...prev, [id]: [] }));
          }
        } catch (e) {
          setRelatedEntitiesMap(prev => ({ ...prev, [id]: [] }));
        }
      }
    })();
  }, [activities]);

  // Process activities for calendar view
  useEffect(() => {
    if (activities.length > 0) {
      const events = activities.map(activity => {
        const events = [];
        
        // Add due date event for planned activities
        if (activity.Status === 'Planned' && activity.DueDate) {
          events.push({
            id: `${activity.ActivityID}-due`,
            title: activity.Subject,
            date: new Date(activity.DueDate),
            type: 'due',
            activity: activity,
            color: 'bg-red-500',
            borderColor: 'border-red-400',
            textColor: 'text-red-800 dark:text-red-200',
            bgColor: 'bg-red-100 dark:bg-red-900/40'
          });
        }
        
        // Add follow-up date event for planned activities
        if (activity.Status === 'Planned' && activity.FollowUpDate) {
          events.push({
            id: `${activity.ActivityID}-followup`,
            title: activity.Subject,
            date: new Date(activity.FollowUpDate),
            type: 'followup',
            activity: activity,
            color: 'bg-orange-500',
            borderColor: 'border-orange-400',
            textColor: 'text-orange-800 dark:text-orange-200',
            bgColor: 'bg-orange-100 dark:bg-orange-900/40'
          });
        }
        
        // Add start date event for done activities
        if (activity.Status === 'Done' && activity.StartDate) {
          events.push({
            id: `${activity.ActivityID}-start`,
            title: activity.Subject,
            date: new Date(activity.StartDate),
            type: 'start',
            activity: activity,
            color: 'bg-green-500',
            borderColor: 'border-green-400',
            textColor: 'text-green-800 dark:text-green-200',
            bgColor: 'bg-green-100 dark:bg-green-900/40'
          });
        }
        
        // Add start date event for in progress activities
        if (activity.Status === 'In Progress' && activity.StartDate) {
          events.push({
            id: `${activity.ActivityID}-start`,
            title: activity.Subject,
            date: new Date(activity.StartDate),
            type: 'start',
            activity: activity,
            color: 'bg-blue-500',
            borderColor: 'border-blue-400',
            textColor: 'text-blue-800 dark:text-blue-200',
            bgColor: 'bg-blue-100 dark:bg-blue-900/40'
          });
        }
        
        return events;
      }).flat();
      
      setCalendarEvents(events);
    }
  }, [activities]);

  // Stats are now calculated in loadActivities() from the loaded list

  // Handle activityId query parameter for scrolling to and opening specific activity
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const activityId = urlParams.get('activityId');
    
    if (activityId && !hasScrolledToActivity && activities.length > 0) {
      const parsedActivityId = parseInt(activityId);
      setFocusedActivityId(parsedActivityId);
      
      // Find the activity in the current list
      const activity = activities.find(a => a.ActivityID === parsedActivityId);
      
      if (activity) {
        // Open the activity detail view
        handleViewActivity(activity);
        setHasScrolledToActivity(true);
      } else {
        // Activity not in current list, try to fetch it directly
        const tenantCode = getProtectedTenantCode();
        const selectedRole = localStorage.getItem('selectedRole');
        fetch(`/api/activities/${activityId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {}),
            ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {})
          }
        })
        .then(res => res.json())
        .then(data => {
          const fetchedActivity = data.activity || data;
          if (fetchedActivity && fetchedActivity.ActivityID) {
            handleViewActivity(fetchedActivity);
            setHasScrolledToActivity(true);
          }
        })
        .catch(err => {
          console.error('Failed to load activity from URL:', err);
          setHasScrolledToActivity(true);
        });
      }
    }
  }, [activities, location.search, hasScrolledToActivity]);

  // Open detail modal when accessing /activities/:id directly
  useEffect(() => {
    const routeId = params.id;
    if (!routeId) return;
    
    // Check if this is a "new" activity with URL parameters
    if (routeId === 'new') {
      const urlParams = new URLSearchParams(location.search);
      const relatedEntityType = urlParams.get('relatedEntityType');
      const relatedEntityId = urlParams.get('relatedEntityId');
      const entityName = urlParams.get('entityName');
      
      if (relatedEntityType && relatedEntityId) {
        // Open form with pre-populated entity relation
        const initialData = {
          relatedEntityType,
          relatedEntityId,
          entityName: entityName || ''
        };
        
        console.log('🔄 Activities: Opening form with initialData from URL params:', initialData);
        setEditingActivity({ _initialData: initialData });
        setShowForm(true);
      } else {
        // Just open form without pre-populated data
        setEditingActivity(null);
        setShowForm(true);
      }
      return;
    }
    
    // Otherwise, load activity details
    (async () => {
      try {
        const tenantCode = getProtectedTenantCode();
        const selectedRole = localStorage.getItem('selectedRole');
        const response = await fetch(`/api/activities/${routeId}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {}),
            ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {})
          }
        });
        if (response.ok) {
          const data = await response.json();
          const act = data.activity || data;
          setSelectedActivity(act);
          setShowDetail(true);
        }
      } catch (_e) {
        // non-fatal
      }
    })();
  }, [params.id, location.search]);

  // Handle filter changes
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

     // Reset filters
   const resetFilters = () => {
     setFilters({
       search: '',
       status: '',
       priority: '',
       entityType: '',
       assignedTo: '',
       overdue: false,
       upcoming: false,
       completed: false,
       inProgress: false
     });
     setPagination(prev => ({ ...prev, page: 1 }));
   };

  // Handle activity actions
  const handleCreateActivity = () => {
    setEditingActivity(null);
    setShowForm(true);
  };

  const handleEditActivity = async (activity) => {
    try {
      // Fetch complete activity data including related entities
      const tenantCode = getProtectedTenantCode();
      const selectedRole = localStorage.getItem('selectedRole');
      const response = await fetch(`/api/activities/${activity.ActivityID}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {}),
          ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {})
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch activity details');
      }

      const data = await response.json();
      setEditingActivity(data.activity);
      setShowForm(true);
    } catch (err) {
      console.error('Error fetching activity details:', err);
      setError(err.message);
      // Fallback to basic activity data
      setEditingActivity(activity);
      setShowForm(true);
    }
  };

  const handleViewActivity = (activity) => {
    setSelectedActivity(activity);
    setShowDetail(true);
  };

  const handleDeleteActivity = async (activityId) => {
    if (!window.confirm(safeT('activities.deleteConfirm', 'Are you sure you want to delete this activity?'))) {
      return;
    }

    try {
      const tenantCode = getProtectedTenantCode();
      const selectedRole = localStorage.getItem('selectedRole');
      const response = await fetch(`/api/activities/${activityId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {}),
          ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {})
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete activity');
      }

      loadActivities();

    } catch (err) {
      setError(err.message);
    }
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingActivity(null);
    loadActivities();
    loadStats();
  };

  const handleExportToCalendar = async () => {
    try {
      setLoading(true);
      const tenantCode = getProtectedTenantCode();

      // Prepare base filters
      const baseFilters = Object.fromEntries(
        Object.entries(filters).filter(([_, value]) => value !== '' && value !== false)
      );

      // Add date range for upcoming activities (next 90 days) if no date filters
      if (!filters.dateFrom && !filters.dateTo) {
        const today = new Date();
        const threeMonthsLater = new Date(today);
        threeMonthsLater.setMonth(threeMonthsLater.getMonth() + 3);
        baseFilters.dateFrom = today.toISOString().split('T')[0];
        baseFilters.dateTo = threeMonthsLater.toISOString().split('T')[0];
      }

      // Fetch all activities (paginated)
      let allActivities = [];
      let currentPage = 1;
      let totalPages = 1;
      const pageLimit = 100; // Maximum allowed by backend

      do {
        const queryParams = new URLSearchParams({
          page: currentPage,
          limit: pageLimit,
          ...baseFilters
        });

        const selectedRole = localStorage.getItem('selectedRole');
        const response = await fetch(`/api/activities?${queryParams}`, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {}),
            ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {})
          }
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || 'Failed to load activities');
        }

        const data = await response.json();
        const pageActivities = data.activities || [];
        allActivities = [...allActivities, ...pageActivities];

        // Update pagination info
        if (data.pagination) {
          totalPages = data.pagination.pages || 1;
        }

        currentPage++;
      } while (currentPage <= totalPages && currentPage <= 5); // Limit to 5 pages (500 activities max)

      if (allActivities.length === 0) {
        alert('No activities found to export');
        return;
      }

      // Show export modal with activities
      setExportActivities(allActivities);
      setSelectedExportIds(new Set(allActivities.map(a => a.ActivityID)));
      setShowExportModal(true);
      setError(null);
    } catch (err) {
      console.error('Export error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmExport = async () => {
    try {
      if (selectedExportIds.size === 0) {
        alert('Please select at least one activity to export');
        return;
      }

      const tenantCode = getProtectedTenantCode();
      const activityIds = Array.from(selectedExportIds).join(',');
      const queryParams = new URLSearchParams({
        activityIds: activityIds
      });

      const selectedRole = localStorage.getItem('selectedRole');
      const response = await fetch(`/api/activities/export/calendar?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {}),
          ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {})
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to export activities');
      }

      // Get the blob and create download link
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `activities-${new Date().toISOString().split('T')[0]}.ics`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      // Close modal and show success message
      setShowExportModal(false);
      alert(safeT('activities.exportSuccess', 'Activities exported successfully! You can now import the .ics file into Google Calendar, Outlook, or any calendar application.'));
    } catch (err) {
      console.error('Export error:', err);
      alert(err.message || 'Failed to export activities');
    }
  };

  const handleExportSingleActivity = async (activityId, e) => {
    if (e) e.stopPropagation();
    
    try {
      const tenantCode = getProtectedTenantCode();
      const queryParams = new URLSearchParams({
        activityIds: activityId.toString()
      });

      const selectedRole = localStorage.getItem('selectedRole');
      const response = await fetch(`/api/activities/export/calendar?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {}),
          ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {})
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to export activity');
      }

      // Get the blob and create download link
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `activity-${activityId}-${new Date().toISOString().split('T')[0]}.ics`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export error:', err);
      alert(err.message || 'Failed to export activity');
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  // Format date with time
  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'Done': return 'text-green-600 bg-green-100';
      case 'In Progress': return 'text-blue-600 bg-blue-100';
      case 'Planned': return 'text-yellow-600 bg-yellow-100';
      case 'On Hold': return 'text-yellow-600 bg-yellow-100';
      case 'Cancelled': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  // Get priority color
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Critical': return 'text-red-600';
      case 'High': return 'text-orange-600';
      case 'Medium': return 'text-blue-600';
      case 'Low': return 'text-green-600';
      default: return 'text-gray-600';
    }
  };

  // Get priority icon
  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'Critical': return <Flag className="h-4 w-4" fill="currentColor" />;
      case 'High': return <Flag className="h-4 w-4" fill="currentColor" />;
      case 'Medium': return <Flag className="h-4 w-4" />;
      case 'Low': return <Flag className="h-4 w-4" />;
      default: return <Flag className="h-4 w-4" />;
    }
  };

  // Check if activity is overdue
  const isOverdue = (dueDate, status) => {
    if (!dueDate || status === 'Done' || status === 'Cancelled') return false;
    return new Date(dueDate) < new Date();
  };

  // Get entity icon
  const getEntityIcon = (entityType) => {
    switch (entityType) {
      case 'Customer': return <Building className="h-4 w-4" />;
      case 'Lead': return <User className="h-4 w-4" />;
      case 'Opportunity': return <Target className="h-4 w-4" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  // Calendar helper functions
  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const getWeekDays = () => {
    return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  };

  const getMonthName = (date) => {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const getEventsForDate = (date) => {
    return calendarEvents.filter(event => {
      const eventDate = new Date(event.date);
      return eventDate.getDate() === date.getDate() &&
             eventDate.getMonth() === date.getMonth() &&
             eventDate.getFullYear() === date.getFullYear();
    });
  };

  const navigateCalendar = (direction) => {
    const newDate = new Date(currentDate);
    if (calendarView === 'month') {
      newDate.setMonth(newDate.getMonth() + direction);
    } else if (calendarView === 'week') {
      newDate.setDate(newDate.getDate() + (direction * 7));
    } else if (calendarView === 'year') {
      newDate.setFullYear(newDate.getFullYear() + direction);
    }
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const showTranslationLoading = !translationsReady || !ready;

  return (
    <div className="space-y-3 sm:space-y-4 md:space-y-6">
      {showTranslationLoading && (
        <div className="text-xs text-gray-500 dark:text-gray-400">
          Loading translations...
        </div>
      )}
      {/* Header - Title and Actions in Same Line */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
        {/* Title Section */}
        <div className="flex-shrink-0">
          <div className="flex items-center space-x-3">
            <img 
              src="icon/activity.png" 
              alt="Activities Icon" 
              className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 object-contain"
            />
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">
              {safeT('activities.title', 'Activities')}
            </h1>
          </div>
          <p className="text-xs sm:text-sm md:text-base text-gray-600 dark:text-gray-300 mt-1">
            {safeT('activities.subtitle', 'Manage and track your activities')}
          </p>
        </div>
        
        {/* Action Buttons - Collapsible Menu */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-2">
          {/* View Mode Toggle - Always Visible */}
          <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-1 w-full sm:w-auto">
            <button
              onClick={() => setViewMode('list')}
              className={`flex-1 sm:flex-none px-2 sm:px-3 py-1.5 rounded-md flex items-center justify-center space-x-1 sm:space-x-2 transition-colors touch-target ${
                viewMode === 'list'
                  ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
              title="List View"
            >
              <List className="h-4 w-4" />
              <span className="text-xs sm:text-sm font-medium">List</span>
            </button>
            <button
              onClick={() => setViewMode('calendar')}
              className={`flex-1 sm:flex-none px-2 sm:px-3 py-1.5 rounded-md flex items-center justify-center space-x-1 sm:space-x-2 transition-colors touch-target ${
                viewMode === 'calendar'
                  ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
              title="Calendar View"
            >
              <Calendar className="h-4 w-4" />
              <span className="text-xs sm:text-sm font-medium">Calendar</span>
            </button>
          </div>
          
          {/* Filter Button - Between View Toggle and Action Buttons */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-3 py-2 rounded-lg flex items-center justify-center space-x-2 text-xs sm:text-sm font-medium touch-target no-tap-highlight transition-all ${
              Object.values(filters).some(value => value !== '' && value !== false)
                ? 'bg-orange-500 dark:bg-orange-600 text-white hover:bg-orange-600 dark:hover:bg-orange-700'
                : showFilters
                ? 'bg-gray-600 dark:bg-gray-500 text-white hover:bg-gray-700 dark:hover:bg-gray-600'
                : 'bg-gray-500 dark:bg-gray-600 text-white hover:bg-gray-600 dark:hover:bg-gray-700'
            }`}
            title="Toggle filters"
          >
            <Filter className="h-4 w-4 flex-shrink-0" />
            <span className="hidden sm:inline">{safeT('common.filter', 'Filter')}</span>
            {Object.values(filters).some(value => value !== '' && value !== false) && (
              <span className="bg-white/20 px-1.5 py-0.5 rounded-full text-xs font-bold">
                {Object.values(filters).filter(value => value !== '' && value !== false).length}
              </span>
            )}
          </button>
          
          {/* Action Buttons Row */}
          <div className="flex flex-wrap items-center justify-end gap-2">
            {/* Expandable Menu Buttons - Always visible on mobile, collapsible on desktop */}
            <div className={`flex flex-wrap items-center gap-2 ${menuExpanded ? 'sm:flex' : 'sm:hidden'}`}>
              <button
                onClick={handleExportToCalendar}
                className="bg-green-600 dark:bg-green-500 text-white px-3 py-2 rounded-lg flex items-center justify-center space-x-1 sm:space-x-2 text-xs sm:text-sm hover:bg-green-700 dark:hover:bg-green-600 touch-target no-tap-highlight"
                title="Export activities to calendar (.ics file)"
              >
                <Download className="h-4 w-4 flex-shrink-0" />
                <span className="hidden sm:inline">{safeT('activities.exportCalendar', 'Export to Calendar')}</span>
                <span className="sm:hidden">Export</span>
              </button>
            </div>
            
            {/* Expand/Collapse Button - Only on Desktop */}
            <button
              onClick={() => setMenuExpanded(!menuExpanded)}
              className="hidden sm:flex bg-gray-500 dark:bg-gray-600 text-white px-3 py-2 rounded-lg hover:bg-gray-600 dark:hover:bg-gray-700 items-center justify-center space-x-1 sm:space-x-2 text-xs sm:text-sm touch-target no-tap-highlight transition-transform"
              title={menuExpanded ? 'Collapse menu' : 'Expand menu'}
            >
              <MoreVertical className="h-4 w-4 flex-shrink-0" />
              <ChevronDown className={`h-3 w-3 flex-shrink-0 transition-transform duration-200 ${menuExpanded ? 'rotate-180' : ''}`} />
            </button>
            
            {/* New Activity Button - Always Visible */}
            <button
              onClick={handleCreateActivity}
              className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 dark:from-teal-600 dark:to-cyan-600 dark:hover:from-teal-700 dark:hover:to-cyan-700 text-white px-4 sm:px-5 py-2.5 sm:py-3 rounded-lg flex items-center justify-center space-x-2 text-sm sm:text-base font-semibold touch-target no-tap-highlight shadow-md transition-all duration-200"
            >
              <Plus className="h-5 w-5 flex-shrink-0" />
              <span>{safeT('activities.newActivity', 'New Activity')}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Statistics Cards - Mobile Optimized */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2 sm:gap-3 md:gap-4">
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm text-gray-600">{safeT('activities.stats.total', 'Total')}</p>
              <p className="text-xl font-semibold text-gray-900">{stats.total || 0}</p>
            </div>
            <Activity className="h-8 w-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm text-gray-600">{safeT('activities.stats.completed', 'Completed')}</p>
              <p className="text-xl font-semibold text-green-600">{stats.completed || 0}</p>
            </div>
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm text-gray-600">{safeT('activities.stats.inProgress', 'In Progress')}</p>
              <p className="text-xl font-semibold text-blue-600">{stats.inProgress || 0}</p>
            </div>
            <RefreshCw className="h-8 w-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm text-gray-600">{safeT('activities.stats.overdue', 'Overdue')}</p>
              <p className="text-xl font-semibold text-red-600">{stats.overdue || 0}</p>
            </div>
            <AlertCircle className="h-8 w-8 text-red-600" />
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm text-gray-600">{safeT('activities.stats.upcoming', 'Upcoming')}</p>
              <p className="text-xl font-semibold text-yellow-600">{stats.upcoming || 0}</p>
            </div>
            <Clock className="h-8 w-8 text-yellow-600" />
          </div>
        </div>

        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs sm:text-sm text-gray-600">{safeT('activities.stats.critical', 'Critical')}</p>
              <p className="text-xl font-semibold text-red-600">{stats.critical || 0}</p>
            </div>
            <Flag className="h-8 w-8 text-red-600" />
          </div>
        </div>
      </div>

      {/* Filters - Hidden by Default */}
      {showFilters && (
        <div className={`p-3 sm:p-4 rounded-lg shadow border transition-colors duration-200 ${
          Object.values(filters).some(value => value !== '' && value !== false)
            ? 'bg-orange-50 dark:bg-orange-900/20 border-orange-300 dark:border-orange-700'
            : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
        }`}>
          {Object.values(filters).some(value => value !== '' && value !== false) && (
            <div className="mb-2 flex items-center gap-2">
              <span className="text-xs font-medium text-orange-700 dark:text-orange-300">
                🔍 Filters Active
              </span>
              <button
                type="button"
                onClick={resetFilters}
                className="text-xs text-orange-600 dark:text-orange-400 hover:underline"
              >
                Clear All
              </button>
            </div>
          )}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-4">
              {/* Search */}
              <div className="relative">
                <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-gray-500" />
                <input
                  type="text"
                  placeholder={safeT('activities.searchActivities', 'Search activities...')}
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                />
              </div>

              {/* Quick Filters */}
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => {
                    // Reset all status filters first
                    const newFilters = { ...filters, overdue: false, upcoming: false, completed: false, inProgress: false };
                    // Then set the clicked one
                    newFilters.overdue = !filters.overdue;
                    setFilters(newFilters);
                    setPagination(prev => ({ ...prev, page: 1 }));
                  }}
                  className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                    filters.overdue
                      ? 'bg-red-100 text-red-700 border-red-300 dark:bg-red-900/30 dark:text-red-300 dark:border-red-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700'
                  }`}
                >
                  <span className="text-xs sm:text-sm">{safeT('activities.stats.overdue', 'Overdue')}</span>
                </button>
                <button
                  onClick={() => {
                    // Reset all status filters first
                    const newFilters = { ...filters, overdue: false, upcoming: false, completed: false, inProgress: false };
                    // Then set the clicked one
                    newFilters.upcoming = !filters.upcoming;
                    setFilters(newFilters);
                    setPagination(prev => ({ ...prev, page: 1 }));
                  }}
                  className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                    filters.upcoming
                      ? 'bg-yellow-100 text-yellow-700 border-yellow-300 dark:bg-yellow-900/30 dark:text-yellow-300 dark:border-yellow-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700'
                  }`}
                >
                  <span className="text-xs sm:text-sm">{safeT('activities.stats.upcoming', 'Upcoming')}</span>
                </button>
                <button
                  onClick={() => {
                    // Reset all status filters first
                    const newFilters = { ...filters, overdue: false, upcoming: false, completed: false, inProgress: false };
                    // Then set the clicked one
                    newFilters.inProgress = !filters.inProgress;
                    setFilters(newFilters);
                    setPagination(prev => ({ ...prev, page: 1 }));
                  }}
                  className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                    filters.inProgress
                      ? 'bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700'
                  }`}
                >
                  <span className="text-xs sm:text-sm">{safeT('activities.stats.inProgress', 'In Progress')}</span>
                </button>
                <button
                  onClick={() => {
                    // Reset all status filters first
                    const newFilters = { ...filters, overdue: false, upcoming: false, completed: false, inProgress: false };
                    // Then set the clicked one
                    newFilters.completed = !filters.completed;
                    setFilters(newFilters);
                    setPagination(prev => ({ ...prev, page: 1 }));
                  }}
                  className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                    filters.completed
                      ? 'bg-green-100 text-green-700 border-green-300 dark:bg-green-900/30 dark:text-green-300 dark:border-green-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700'
                  }`}
                >
                  <span className="text-xs sm:text-sm">{safeT('activities.stats.completed', 'Completed')}</span>
                </button>
              </div>
            </div>
          </div>

          {/* Advanced Filters */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{safeT('activities.status', 'Status')}</label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">{safeT('activities.allStatuses', 'All Statuses')}</option>
                <option value="Planned">{safeT('activities.statuses.planned', 'Planned')}</option>
                <option value="In Progress">{safeT('activities.statuses.inProgress', 'In Progress')}</option>
                <option value="Done">{safeT('activities.statuses.done', 'Done')}</option>
                <option value="On Hold">{safeT('activities.statuses.onHold', 'On Hold')}</option>
                <option value="Cancelled">{safeT('activities.statuses.cancelled', 'Cancelled')}</option>
              </select>
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{safeT('activities.priority', 'Priority')}</label>
              <select
                value={filters.priority}
                onChange={(e) => handleFilterChange('priority', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">{safeT('activities.allPriorities', 'All Priorities')}</option>
                <option value="Low">{safeT('activities.priorities.low', 'Low')}</option>
                <option value="Medium">{safeT('activities.priorities.medium', 'Medium')}</option>
                <option value="High">{safeT('activities.priorities.high', 'High')}</option>
                <option value="Critical">{safeT('activities.priorities.critical', 'Critical')}</option>
              </select>
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{safeT('common.type', 'Type')}</label>
              <select
                value={filters.entityType}
                onChange={(e) => handleFilterChange('entityType', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">{safeT('activities.allEntityTypes', 'All Entity Types')}</option>
                <option value="Customer">{safeT('activities.entityTypes.customer', 'Customer')}</option>
                <option value="Lead">{safeT('activities.entityTypes.lead', 'Lead')}</option>
                <option value="Opportunity">{safeT('activities.entityTypes.opportunity', 'Opportunity')}</option>
              </select>
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{safeT('activities.activityType', 'Activity Type')}</label>
              <select
                value={filters.activityType}
                onChange={(e) => handleFilterChange('activityType', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 dark:focus:border-blue-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              >
                <option value="">{safeT('activities.allTypes', 'All Types')}</option>
                <option value="Call">{safeT('activities.types.call', 'Call')}</option>
                <option value="Email">{safeT('activities.types.email', 'Email')}</option>
                <option value="Meeting">{safeT('activities.types.meeting', 'Meeting')}</option>
                <option value="Task">{safeT('activities.types.task', 'Task')}</option>
                <option value="Follow-up">{safeT('activities.types.followUp', 'Follow-up')}</option>
                <option value="Demo">{safeT('activities.types.demo', 'Demo')}</option>
                <option value="Proposal">{safeT('activities.types.proposal', 'Proposal')}</option>
                <option value="Other">{safeT('activities.types.other', 'Other')}</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Calendar View Controls - Only show when in calendar mode */}
      {viewMode === 'calendar' && (
        <div className="flex items-center justify-between bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCalendarView('year')}
                className={`px-3 py-1 text-sm rounded border transition-colors ${
                  calendarView === 'year'
                    ? 'bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600'
                }`}
              >
                <span className="text-xs sm:text-sm">{safeT('activities.year', 'Year')}</span>
              </button>
              <button
                onClick={() => setCalendarView('month')}
                className={`px-3 py-1 text-sm rounded border transition-colors ${
                  calendarView === 'month'
                    ? 'bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600'
                }`}
              >
                <span className="text-xs sm:text-sm">{safeT('activities.month', 'Month')}</span>
              </button>
              <button
                onClick={() => setCalendarView('week')}
                className={`px-3 py-1 text-sm rounded border transition-colors ${
                  calendarView === 'week'
                    ? 'bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-600'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600'
                }`}
              >
                <span className="text-xs sm:text-sm">{safeT('activities.week', 'Week')}</span>
              </button>
            </div>
            <button
              onClick={goToToday}
              className="px-3 py-1 text-sm text-blue-600 hover:text-blue-700 border border-blue-300 rounded hover:bg-blue-50 dark:text-blue-400 dark:border-blue-600 dark:hover:bg-blue-900/30"
            >
              <span className="text-xs sm:text-sm">{safeT('activities.today', 'Today')}</span>
            </button>
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
          <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {/* Activities List or Calendar */}
      {viewMode === 'list' ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <RefreshCw className="h-6 w-6 animate-spin text-blue-600" />
            <span className="ml-2 text-xs sm:text-sm text-gray-600">{safeT('activities.loadingActivities', 'Loading activities...')}</span>
          </div>
        ) : activities.length === 0 ? (
          <div className="text-center py-12">
            <Activity className="h-12 w-12 mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 text-xs sm:text-sm md:text-lg">{safeT('activities.noActivities', 'No activities found')}</p>
            <p className="text-gray-400 text-xs sm:text-sm mb-4">
              {Object.values(filters).some(v => v !== '' && v !== false)
                ? safeT('activities.tryAdjustingFilters', 'Try adjusting your filters')
                : safeT('activities.createFirstActivity', 'Create your first activity')
              }
            </p>
            <button
              onClick={handleCreateActivity}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center space-x-2 mx-auto"
            >
              <Plus className="h-4 w-4" />
              <span>{t('activities.createActivity')}</span>
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Activity
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Related To
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Priority
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    {t('activities.dueDate')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    {t('activities.assignedTo')}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    {t('activities.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                {activities.map((activity) => (
                  <tr
                    key={activity.ActivityID}
                    ref={el => activityRefs.current[activity.ActivityID] = el}
                    className={`hover:bg-gray-50 dark:hover:bg-gray-800 ${
                      isOverdue(activity.DueDate, activity.Status) ? 'bg-red-300 dark:bg-red-900/30' : ''
                    } ${
                      focusedActivityId === activity.ActivityID ? 'ring-2 ring-yellow-400 ring-opacity-50' : ''
                    }`}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-start space-x-3">
                        {activity.ParentActivityID && (
                          <div className="flex items-center text-gray-400 dark:text-gray-500 mr-2">
                            <ArrowRight className="h-4 w-4" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleViewActivity(activity)}
                              className={`text-sm font-medium truncate hover:text-blue-600 dark:hover:text-blue-400 hover:underline cursor-pointer text-left ${
                                isOverdue(activity.DueDate, activity.Status) ? 'text-red-800 dark:text-red-400' : 'text-gray-900 dark:text-gray-100'
                              }`}
                            >
                              {activity.Subject}
                            </button>
                            {activity.GoogleCalendarSynced && (
                              <span 
                                className="text-blue-600 dark:text-blue-400" 
                                title={`Synced with Google Calendar${activity.LastGoogleSyncAt ? ` at ${new Date(activity.LastGoogleSyncAt).toLocaleString()}` : ''}`}
                              >
                                <Calendar className="h-3.5 w-3.5" />
                              </span>
                            )}
                            {activity.ChildActivitiesCount > 0 && (
                              <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-200 text-xs px-2 py-1 rounded-full">
                                {activity.ChildActivitiesCount} sub
                              </span>
                            )}
                          </div>
                          <p className={`text-sm truncate ${
                            isOverdue(activity.DueDate, activity.Status) ? 'text-red-700 dark:text-red-300' : 'text-gray-500 dark:text-gray-400'
                          }`}>{activity.ActivityType}</p>
                          {activity.Description && (
                            <p className={`text-xs truncate mt-1 max-w-xs ${
                              isOverdue(activity.DueDate, activity.Status) ? 'text-red-600 dark:text-red-400' : 'text-gray-400 dark:text-gray-500'
                            }`}>
                              {activity.Description}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {relatedEntitiesMap[activity.ActivityID] && relatedEntitiesMap[activity.ActivityID].length > 0 ? (
                        <div className="space-y-1">
                          {relatedEntitiesMap[activity.ActivityID].map((rel, idx) => (
                            <div key={idx} className="flex items-center space-x-2">
                              {getEntityIcon(rel.EntityType)}
                              <div>
                                <p className={`text-sm font-medium ${
                                  isOverdue(activity.DueDate, activity.Status) ? 'text-red-800 dark:text-red-400' : 'text-gray-900 dark:text-gray-100'
                                }`}>
                                  {rel.EntityName || t('activities.unknown')}
                                </p>
                                <p className={`text-xs ${
                                  isOverdue(activity.DueDate, activity.Status) ? 'text-red-700 dark:text-red-300' : 'text-gray-500 dark:text-gray-400'
                                }`}>{rel.EntityType}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="flex items-center space-x-2">
                          {getEntityIcon(activity.RelatedEntityType)}
                          <div>
                            <p className={`text-sm font-medium ${
                              isOverdue(activity.DueDate, activity.Status) ? 'text-red-800 dark:text-red-400' : 'text-gray-900 dark:text-gray-100'
                            }`}>
                              {activity.RelatedEntityName || t('activities.unknown')}
                            </p>
                            <p className={`text-xs ${
                              isOverdue(activity.DueDate, activity.Status) ? 'text-red-700 dark:text-red-300' : 'text-gray-500 dark:text-gray-400'
                            }`}>{activity.RelatedEntityType}</p>
                          </div>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(activity.Status)}`}>
                        {activity.Status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`flex items-center space-x-1 ${getPriorityColor(activity.Priority)}`}>
                        {getPriorityIcon(activity.Priority)}
                        <span className={`text-sm font-medium ${
                          isOverdue(activity.DueDate, activity.Status) ? 'text-red-800 dark:text-red-400' : ''
                        }`}>{activity.Priority}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className={`text-sm ${
                        isOverdue(activity.DueDate, activity.Status) ? 'text-red-800 dark:text-red-400' : 'text-gray-900 dark:text-gray-100'
                      }`}>
                        {activity.DueDate ? (
                          <div className={isOverdue(activity.DueDate, activity.Status) ? 'text-red-800 dark:text-red-400' : ''}>
                            {formatDate(activity.DueDate)}
                            {isOverdue(activity.DueDate, activity.Status) && (
                              <div className="text-xs text-red-700 dark:text-red-300 font-medium">{t('activities.overdue')}</div>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400 dark:text-gray-500">{t('activities.noDueDate')}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-gray-100">
                        {activity.AssignedToName ? (
                          <div className="flex items-center space-x-1">
                            <UserCheck className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                            <span className={isOverdue(activity.DueDate, activity.Status) ? 'text-red-800 dark:text-red-400' : ''}>
                              {activity.AssignedToName}
                            </span>
                          </div>
                        ) : (
                          <span className="text-gray-400 dark:text-gray-500">-</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleViewActivity(activity)}
                          className="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300"
                          title="View Details"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={(e) => handleExportSingleActivity(activity.ActivityID, e)}
                          className="text-green-600 dark:text-green-400 hover:text-green-900 dark:hover:text-green-300"
                          title="Download as .ics file"
                        >
                          <Download className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleEditActivity(activity)}
                          className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-300"
                          title="Edit"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteActivity(activity.ActivityID)}
                          className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {activities.length > 0 && pagination.pages > 1 && (
          <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <p className="text-sm text-gray-700">
                  {t('activities.showing')} {((pagination.page - 1) * pagination.limit) + 1} {t('activities.to')}{' '}
                  {Math.min(pagination.page * pagination.limit, pagination.total)} {t('activities.of')}{' '}
                  {pagination.total} {t('activities.results')}
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page === 1}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  {t('activities.previous')}
                </button>
                <span className="text-sm text-gray-700">
                  {t('activities.page')} {pagination.page} {t('activities.of')} {pagination.pages}
                </span>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page === pagination.pages}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  {t('activities.next')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      ) : (
                 /* Calendar View */
         <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
           {loading ? (
             <div className="flex justify-center items-center py-12">
               <RefreshCw className="h-6 w-6 animate-spin text-blue-600" />
               <span className="ml-2 text-gray-600 dark:text-gray-400">{t('activities.loadingCalendar')}</span>
             </div>
           ) : (
             <div className="p-6">
               {/* Calendar Header */}
               <div className="flex items-center justify-between mb-6">
                 <div className="flex items-center space-x-4">
                   <button
                     onClick={() => navigateCalendar(-1)}
                     className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                   >
                     <ChevronLeft className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                   </button>
                   <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                     {calendarView === 'year' ? currentDate.getFullYear() : getMonthName(currentDate)}
                   </h2>
                   <button
                     onClick={() => navigateCalendar(1)}
                     className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                   >
                     <ChevronRight className="h-5 w-5 text-gray-600 dark:text-gray-400" />
                   </button>
                 </div>
               </div>

               {/* Calendar Grid */}
               {calendarView === 'month' && (
                 <div className="grid grid-cols-7 gap-1">
                   {/* Week day headers */}
                   {getWeekDays().map(day => (
                     <div key={day} className="p-2 text-center text-sm font-medium text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 rounded">
                       {day}
                     </div>
                   ))}

                   {/* Calendar days */}
                   {(() => {
                     const daysInMonth = getDaysInMonth(currentDate);
                     const firstDayOfMonth = getFirstDayOfMonth(currentDate);
                     const days = [];

                     // Add empty cells for days before the first day of the month
                     for (let i = 0; i < firstDayOfMonth; i++) {
                       days.push(<div key={`empty-${i}`} className="p-2 min-h-[100px] bg-gray-50 dark:bg-gray-700"></div>);
                     }

                     // Add cells for each day of the month
                     for (let day = 1; day <= daysInMonth; day++) {
                       const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
                       const events = getEventsForDate(date);
                       const isToday = date.toDateString() === new Date().toDateString();

                       days.push(
                         <div
                           key={day}
                           className={`p-2 min-h-[100px] border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors ${
                             isToday ? 'bg-blue-50 dark:bg-blue-900/40 border-blue-400 dark:border-blue-500 shadow-sm' : 'bg-white dark:bg-gray-800'
                           }`}
                           onClick={() => {
                             // Create new activity for this date
                             setEditingActivity({
                               dueDate: date.toISOString().split('T')[0],
                               startDate: date.toISOString().split('T')[0]
                             });
                             setShowForm(true);
                           }}
                         >
                           <div className="flex items-center justify-between mb-2">
                             <span className={`text-sm font-medium ${
                               isToday ? 'text-blue-700 dark:text-blue-300' : 'text-gray-900 dark:text-white'
                             }`}>
                               {day}
                             </span>
                             {events.length > 0 && (
                               <span className={`text-xs font-medium ${
                                 isToday ? 'text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-300'
                               }`}>
                                 {events.length} event{events.length > 1 ? 's' : ''}
                               </span>
                             )}
                           </div>
                           
                          {/* Events for this day */}
                          <div className="space-y-1">
                            {events.slice(0, 3).map((event, index) => (
                              <div
                                key={event.id}
                                className={`p-1.5 rounded-md text-xs cursor-pointer transition-all hover:shadow-sm ${event.bgColor} ${event.borderColor} border-l-4 ${event.textColor} relative group`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleViewActivity(event.activity);
                                }}
                                title={`${event.title} (${event.type})`}
                              >
                                <div className="flex items-start justify-between gap-1">
                                  <div className="flex-1 min-w-0">
                                    <div className="truncate font-medium leading-tight">{event.title}</div>
                                    <div className="text-xs opacity-80 capitalize mt-0.5">{event.type}</div>
                                  </div>
                                  <button
                                    onClick={(e) => handleExportSingleActivity(event.activity.ActivityID, e)}
                                    className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 hover:bg-white/20 rounded"
                                    title="Download as .ics file"
                                  >
                                    <Download className="h-3 w-3" />
                                  </button>
                                </div>
                              </div>
                            ))}
                             {events.length > 3 && (
                               <div className="text-xs text-gray-600 dark:text-gray-300 text-center font-medium bg-gray-100 dark:bg-gray-700 rounded px-1 py-0.5">
                                 +{events.length - 3} more
                               </div>
                             )}
                           </div>
                         </div>
                       );
                     }

                     return days;
                   })()}
                 </div>
               )}

               {/* Week View */}
               {calendarView === 'week' && (
                 <div className="grid grid-cols-7 gap-1">
                   {/* Week day headers */}
                   {getWeekDays().map(day => (
                     <div key={day} className="p-2 text-center text-sm font-medium text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 rounded">
                       {day}
                     </div>
                   ))}

                   {/* Week days */}
                   {(() => {
                     const startOfWeek = new Date(currentDate);
                     const dayOfWeek = startOfWeek.getDay();
                     startOfWeek.setDate(startOfWeek.getDate() - dayOfWeek);
                     
                     const days = [];
                     for (let i = 0; i < 7; i++) {
                       const date = new Date(startOfWeek);
                       date.setDate(startOfWeek.getDate() + i);
                       const events = getEventsForDate(date);
                       const isToday = date.toDateString() === new Date().toDateString();

                       days.push(
                         <div
                           key={i}
                           className={`p-2 min-h-[120px] border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors ${
                             isToday ? 'bg-blue-50 dark:bg-blue-900/40 border-blue-400 dark:border-blue-500 shadow-sm' : 'bg-white dark:bg-gray-800'
                           }`}
                           onClick={() => {
                             setEditingActivity({
                               dueDate: date.toISOString().split('T')[0],
                               startDate: date.toISOString().split('T')[0]
                             });
                             setShowForm(true);
                           }}
                         >
                           <div className="flex items-center justify-between mb-2">
                             <span className={`text-sm font-medium ${
                               isToday ? 'text-blue-700 dark:text-blue-300' : 'text-gray-900 dark:text-white'
                             }`}>
                               {date.getDate()}
                             </span>
                             {events.length > 0 && (
                               <span className={`text-xs font-medium ${
                                 isToday ? 'text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-300'
                               }`}>
                                 {events.length} event{events.length > 1 ? 's' : ''}
                               </span>
                             )}
                           </div>
                           
                           {/* Events for this day */}
                           <div className="space-y-1">
                             {events.slice(0, 4).map((event, index) => (
                               <div
                                 key={event.id}
                                 className={`p-1.5 rounded-md text-xs cursor-pointer transition-all hover:shadow-sm ${event.bgColor} ${event.borderColor} border-l-4 ${event.textColor}`}
                                 onClick={(e) => {
                                   e.stopPropagation();
                                   handleViewActivity(event.activity);
                                 }}
                                 title={`${event.title} (${event.type})`}
                               >
                                 <div className="truncate font-medium leading-tight">{event.title}</div>
                                 <div className="text-xs opacity-80 capitalize mt-0.5">{event.type}</div>
                               </div>
                             ))}
                             {events.length > 4 && (
                               <div className="text-xs text-gray-600 dark:text-gray-300 text-center font-medium bg-gray-100 dark:bg-gray-700 rounded px-1 py-0.5">
                                 +{events.length - 4} more
                               </div>
                             )}
                           </div>
                         </div>
                       );
                     }
                     return days;
                   })()}
                 </div>
               )}

               {/* Year View */}
               {calendarView === 'year' && (
                 <div className="grid grid-cols-3 gap-4">
                   {(() => {
                     const months = [];
                     for (let month = 0; month < 12; month++) {
                       const monthDate = new Date(currentDate.getFullYear(), month, 1);
                       const monthName = monthDate.toLocaleDateString('en-US', { month: 'short' });
                       const daysInMonth = getDaysInMonth(monthDate);
                       const firstDayOfMonth = getFirstDayOfMonth(monthDate);
                       const isCurrentMonth = month === new Date().getMonth() && currentDate.getFullYear() === new Date().getFullYear();

                       months.push(
                         <div
                           key={month}
                           className={`p-3 border border-gray-200 dark:border-gray-600 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                             isCurrentMonth ? 'bg-blue-50 dark:bg-blue-900/40 border-blue-400 dark:border-blue-500 shadow-sm' : 'bg-white dark:bg-gray-800'
                           }`}
                           onClick={() => {
                             setCurrentDate(new Date(currentDate.getFullYear(), month, 1));
                             setCalendarView('month');
                           }}
                         >
                           <div className="text-center mb-2">
                             <h3 className={`text-sm font-medium ${
                               isCurrentMonth ? 'text-blue-600 dark:text-blue-400' : 'text-gray-900 dark:text-white'
                             }`}>
                               {monthName}
                             </h3>
                           </div>
                           <div className="grid grid-cols-7 gap-1 text-xs">
                             {getWeekDays().map(day => (
                               <div key={day} className="text-center text-gray-500 dark:text-gray-400">
                                 {day.charAt(0)}
                               </div>
                             ))}
                             {(() => {
                               const days = [];
                               // Add empty cells
                               for (let i = 0; i < firstDayOfMonth; i++) {
                                 days.push(<div key={`empty-${i}`} className="h-4"></div>);
                               }
                               // Add day numbers
                               for (let day = 1; day <= daysInMonth; day++) {
                                 const date = new Date(currentDate.getFullYear(), month, day);
                                 const events = getEventsForDate(date);
                                 const isToday = date.toDateString() === new Date().toDateString();
                                 
                                 days.push(
                                   <div
                                     key={day}
                                     className={`h-4 text-center text-xs ${
                                       isToday ? 'text-blue-600 dark:text-blue-400 font-bold' : 
                                       events.length > 0 ? 'text-gray-900 dark:text-white font-medium' : 'text-gray-500 dark:text-gray-400'
                                     }`}
                                   >
                                     {day}
                                   </div>
                                 );
                               }
                               return days;
                             })()}
                           </div>
                         </div>
                       );
                     }
                     return months;
                   })()}
                 </div>
               )}

               {/* Calendar Legend */}
               <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-600">
                 <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">{t('activities.legend')}</h3>
                 <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                   <div className="flex items-center space-x-3">
                     <div className="w-4 h-4 bg-red-100 dark:bg-red-900/40 border-l-4 border-red-400 rounded-sm"></div>
                     <span className="text-sm text-gray-700 dark:text-gray-300">{t('activities.dueDatePlanned')}</span>
                   </div>
                   <div className="flex items-center space-x-3">
                     <div className="w-4 h-4 bg-orange-100 dark:bg-orange-900/40 border-l-4 border-orange-400 rounded-sm"></div>
                     <span className="text-sm text-gray-700 dark:text-gray-300">{t('activities.followUpPlanned')}</span>
                   </div>
                   <div className="flex items-center space-x-3">
                     <div className="w-4 h-4 bg-blue-100 dark:bg-blue-900/40 border-l-4 border-blue-400 rounded-sm"></div>
                     <span className="text-sm text-gray-700 dark:text-gray-300">{t('activities.startDateInProgress')}</span>
                   </div>
                   <div className="flex items-center space-x-3">
                     <div className="w-4 h-4 bg-green-100 dark:bg-green-900/40 border-l-4 border-green-400 rounded-sm"></div>
                     <span className="text-sm text-gray-700 dark:text-gray-300">{t('activities.startDateDone')}</span>
                   </div>
                 </div>
               </div>
             </div>
           )}
         </div>
      )}

      {/* Modals */}
      {showForm && (
        <ActivityForm
          key={editingActivity?.ActivityID || 'new'}
          activity={editingActivity?._initialData ? null : editingActivity}
          initialData={editingActivity?._initialData || null}
          onClose={() => {
            setShowForm(false);
            setEditingActivity(null);
          }}
          onSuccess={handleFormSuccess}
        />
      )}

      {showDetail && selectedActivity && (
        <ActivityDetail
          activity={selectedActivity}
          onClose={() => {
            setShowDetail(false);
            setSelectedActivity(null);
          }}
          onEdit={() => {
            setShowDetail(false);
            handleEditActivity(selectedActivity);
          }}
          onDelete={() => {
            setShowDetail(false);
            handleDeleteActivity(selectedActivity.ActivityID);
          }}
        />
      )}

      {/* Export Modal */}
      {showExportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {t('activities.exportToCalendar', 'Export to Calendar')}
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  Select activities to export ({selectedExportIds.size} of {exportActivities.length} selected)
                </p>
              </div>
              <button
                onClick={() => setShowExportModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Selection Controls */}
            <div className="px-6 py-3 bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setSelectedExportIds(new Set(exportActivities.map(a => a.ActivityID)))}
                  className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                >
                  Select All
                </button>
                <span className="text-gray-400">|</span>
                <button
                  onClick={() => setSelectedExportIds(new Set())}
                  className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                >
                  Deselect All
                </button>
              </div>
            </div>

            {/* Activities List */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-2">
                {exportActivities.map((activity) => (
                  <label
                    key={activity.ActivityID}
                    className={`flex items-start p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                      selectedExportIds.has(activity.ActivityID)
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedExportIds.has(activity.ActivityID)}
                      onChange={(e) => {
                        const newSet = new Set(selectedExportIds);
                        if (e.target.checked) {
                          newSet.add(activity.ActivityID);
                        } else {
                          newSet.delete(activity.ActivityID);
                        }
                        setSelectedExportIds(newSet);
                      }}
                      className="mt-1 h-4 w-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                    />
                    <div className="ml-3 flex-1">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                            {activity.Subject}
                          </h4>
                          <div className="flex items-center space-x-3 mt-1 text-xs text-gray-500 dark:text-gray-400">
                            <span className="flex items-center">
                              <Calendar className="h-3 w-3 mr-1" />
                              {formatDate(activity.DueDate)}
                            </span>
                            <span className={`inline-flex px-2 py-0.5 rounded-full font-medium ${
                              activity.Status === 'Done' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' :
                              activity.Status === 'In Progress' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' :
                              activity.Status === 'Cancelled' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' :
                              'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                            }`}>
                              {activity.Status}
                            </span>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full font-medium ${
                              activity.Priority === 'Critical' ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' :
                              activity.Priority === 'High' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300' :
                              activity.Priority === 'Medium' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' :
                              'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                            }`}>
                              <Flag className="h-3 w-3 mr-1" />
                              {activity.Priority}
                            </span>
                          </div>
                          {activity.Description && (
                            <p className="text-xs text-gray-600 dark:text-gray-400 mt-2 line-clamp-2">
                              {activity.Description}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end space-x-3">
              <button
                onClick={() => setShowExportModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                onClick={handleConfirmExport}
                disabled={selectedExportIds.size === 0}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                <Download className="h-4 w-4" />
                <span>{t('activities.export', 'Export')} ({selectedExportIds.size})</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Activities; 